import React, { useState, useEffect } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import api from '../../../services/api';

const Step7Qualification = () => {
    const { mockUser, refreshUser } = useOutletContext();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    
    // Lookups
    const [stateList, setStateList] = useState([]);
    const [districts10, setDistricts10] = useState([]);
    const [districts12, setDistricts12] = useState([]);

    // Initial State Structure
    const initialClassState = {
        passStatus: '1', // 1=Passed, 2=Appearing
        year: '',
        examName: '10', 
        schoolPlace: '0', 
        schoolType: '0', 
        country: 'India', 
        state: '0', 
        district: '0', 
        board: '0', 
        resultMode: '0', 
        totalMarks: '', 
        obtainedMarks: '', 
        percentage: '', 
        rollNo: '', 
        instituteName: '', 
        pincode: ''
    };

    const [x10, setX10] = useState({ ...initialClassState });
    const [x12, setX12] = useState({ ...initialClassState, examName: '12' }); // Default exam name for 12th
    const [captcha, setCaptcha] = useState('');

    // Load Data
    useEffect(() => {
        const init = async () => {
            try {
                // States Lookup
                const stateRes = await api.get('/mock-cuet/states');
                if (stateRes.data.success) setStateList(stateRes.data.data);

                // User Data
                const res = await api.get(`/mock-cuet/application-data?mockAppId=${mockUser.id}`);
                if (res.data.success) {
                    const db = res.data.data;
                    
                    // Map Class 10
                    setX10({
                        passStatus: db.x10_pass_status || '1',
                        year: db.x10_year || '2016',
                        examName: db.x10_exam_name || '10',
                        schoolPlace: db.x10_school_place || '0',
                        schoolType: db.x10_school_type || '0',
                        country: db.x10_school_country || 'India',
                        state: db.x10_school_state || '0',
                        district: db.x10_school_district || '0',
                        board: db.x10_board_name || '0',
                        resultMode: db.x10_result_mode || '0',
                        totalMarks: db.x10_total_marks || '',
                        obtainedMarks: db.x10_obtained_marks || '',
                        percentage: db.x10_percentage || '',
                        rollNo: db.x10_roll_no || '',
                        instituteName: db.x10_institute_name || '',
                        pincode: db.x10_school_pincode || ''
                    });

                    // Map Class 12
                    setX12({
                        passStatus: db.x12_pass_status || '2', // Default appearing for 12th
                        year: db.x12_year || '2026',
                        examName: db.x12_exam_name || '12',
                        schoolPlace: db.x12_school_place || '0',
                        schoolType: db.x12_school_type || '0',
                        country: db.x12_school_country || 'India',
                        state: db.x12_school_state || '0',
                        district: db.x12_school_district || '0',
                        board: db.x12_board_name || '0',
                        resultMode: db.x12_result_mode || '0',
                        totalMarks: db.x12_total_marks || '',
                        obtainedMarks: db.x12_obtained_marks || '',
                        percentage: db.x12_percentage || '',
                        rollNo: db.x12_roll_no || '',
                        instituteName: db.x12_institute_name || '',
                        pincode: db.x12_school_pincode || ''
                    });
                }
            } catch (err) { console.error(err); }
        };
        init();
    }, [mockUser]);

    // Dynamic District Fetching
    const fetchDistricts = async (stateCode, setFunc) => {
        if (!stateCode || stateCode === '0') { setFunc([]); return; }
        try {
            const res = await api.get(`/mock-cuet/districts/${stateCode}`);
            if (res.data.success) setFunc(res.data.data);
        } catch (e) {}
    };

    useEffect(() => { fetchDistricts(x10.state, setDistricts10); }, [x10.state]);
    useEffect(() => { fetchDistricts(x12.state, setDistricts12); }, [x12.state]);

    // Handlers
    const handleChange = (setter, currentState, e) => {
        const { name, value } = e.target;
        setter(prev => {
            const newState = { ...prev, [name]: value };
            
            // Auto Calculate Percentage
            if (name === 'totalMarks' || name === 'obtainedMarks') {
                const total = name === 'totalMarks' ? value : prev.totalMarks;
                const obtd = name === 'obtainedMarks' ? value : prev.obtainedMarks;
                if(total && obtd && !isNaN(total) && !isNaN(obtd) && Number(total) > 0) {
                    newState.percentage = ((Number(obtd) / Number(total)) * 100).toFixed(2);
                }
            }
            return newState;
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const res = await api.post('/mock-cuet/save-step', {
                mockAppId: mockUser.id,
                stepNumber: 7,
                stepData: { x10, x12 }, // Nested object structure
                nextStepNumber: 8
            });

            if (res.data.success) {
                refreshUser(8);
                navigate('/services/counselling/dashboard/additional-details');
            }
        } catch (err) {
            alert("Error saving qualification details.");
        } finally {
            setLoading(false);
        }
    };

    // Helper Component for a Qualification Block to reduce repetition
    const QualificationBlock = ({ title, data, setData, districtOptions }) => (
        <fieldset className="the-fieldset" style={{border: '1px solid #ccc', padding: '10px', marginBottom: '20px'}}>
            <legend className="the-legend" style={{width:'auto', border:'none', fontSize:'16px', fontWeight:'bold', marginLeft:'10px'}}>
                {title}
            </legend>
            <div className="formLabelNormal">
                <div className="row">
                    <div className="col-sm-6 form-group">
                        <label>उत्तीर्ण की स्थिति (Pass Status)</label>
                        <select name="passStatus" className="form-control" value={data.passStatus} onChange={(e) => handleChange(setData, data, e)}>
                            <option value="1">Passed</option>
                            <option value="2">Appearing</option>
                        </select>
                    </div>
                    <div className="col-sm-6 form-group">
                        <label>उत्तीर्ण / सम्मिलित होने का वर्ष (Year)</label>
                        <select name="year" className="form-control" value={data.year} onChange={(e) => handleChange(setData, data, e)}>
                            <option value="0">Select</option>
                            {[...Array(20)].map((_,i) => <option key={i} value={2026-i}>{2026-i}</option>)}
                        </select>
                    </div>
                </div>

                <div className="row">
                    <div className="col-sm-6 form-group">
                        <label>योग्यता परीक्षा (Qualifying Exam)</label>
                        <select name="examName" className="form-control" value={data.examName} onChange={(e) => handleChange(setData, data, e)}>
                            <option value="10">10th or Equivalent</option>
                            <option value="12">12th or Equivalent</option>
                        </select>
                    </div>
                    <div className="col-sm-6 form-group">
                        <label>स्कूल का स्थान (Place of School)</label>
                        <select name="schoolPlace" className="form-control" value={data.schoolPlace} onChange={(e) => handleChange(setData, data, e)}>
                            <option value="0">Select</option>
                            <option value="Rural">Rural</option>
                            <option value="Urban">Urban</option>
                        </select>
                    </div>
                </div>

                <div className="row">
                    <div className="col-sm-6 form-group">
                        <label>स्कूल / कॉलेज का प्रकार (Type of School)</label>
                        <select name="schoolType" className="form-control" value={data.schoolType} onChange={(e) => handleChange(setData, data, e)}>
                            <option value="0">Select</option>
                            <option value="Govt">Government School</option>
                            <option value="Private">Private Unaided</option>
                            <option value="Aided">Govt Aided Private</option>
                            <option value="KV">Kendriya Vidyalaya</option>
                            <option value="JNV">Jawahar Navodaya Vidyalaya</option>
                        </select>
                    </div>
                    <div className="col-sm-6 form-group">
                        <label>योग्यता परीक्षा राज्य (Exam State)</label>
                        <select name="state" className="form-control" value={data.state} onChange={(e) => handleChange(setData, data, e)}>
                            <option value="0">Select</option>
                            {stateList.map((st) => (
                                <option key={st.state_code} value={st.state_code}>{st.state_name}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="row">
                    <div className="col-sm-6 form-group">
                        <label>योग्यता परीक्षा जिला (Exam District)</label>
                        <select name="district" className="form-control" value={data.district} onChange={(e) => handleChange(setData, data, e)}>
                            <option value="0">Select</option>
                            {districtOptions.map((dist) => (
                                <option key={dist.district_code} value={dist.district_code}>{dist.district_name}</option>
                            ))}
                        </select>
                    </div>
                    <div className="col-sm-6 form-group">
                        <label>स्कूल बोर्ड (School Board)</label>
                        <select name="board" className="form-control" value={data.board} onChange={(e) => handleChange(setData, data, e)}>
                            <option value="0">Select</option>
                            <option value="CBSE">Central Board of Secondary Education</option>
                            <option value="ICSE">Council for Indian School Certificate</option>
                            <option value="State">State Board</option>
                            <option value="NIOS">National Institute of Open Schooling</option>
                        </select>
                    </div>
                </div>

                <div className="row">
                    <div className="col-sm-6 form-group">
                        <label>परिणाम मोड (Result Mode)</label>
                        <select name="resultMode" className="form-control" value={data.resultMode} onChange={(e) => handleChange(setData, data, e)}>
                            <option value="0">Select</option>
                            <option value="1">Percentage</option>
                            <option value="2">CGPA</option>
                        </select>
                    </div>
                </div>

                {data.resultMode === '1' && (
                    <div className="row">
                        <div className="col-sm-4 form-group">
                            <label>Total Marks</label>
                            <input type="text" name="totalMarks" className="form-control" value={data.totalMarks} onChange={(e) => handleChange(setData, data, e)} />
                        </div>
                        <div className="col-sm-4 form-group">
                            <label>Obtained Marks</label>
                            <input type="text" name="obtainedMarks" className="form-control" value={data.obtainedMarks} onChange={(e) => handleChange(setData, data, e)} />
                        </div>
                        <div className="col-sm-4 form-group">
                            <label>Marks (%)</label>
                            <input type="text" name="percentage" className="form-control" value={data.percentage} readOnly />
                        </div>
                    </div>
                )}

                <div className="row">
                    <div className="col-sm-6 form-group">
                        <label>रोल नंबर (Roll No)</label>
                        <input type="text" name="rollNo" className="form-control" value={data.rollNo} onChange={(e) => handleChange(setData, data, e)} />
                    </div>
                    <div className="col-sm-6 form-group">
                        <label>स्कूल / कॉलेज का नाम और पता (School Name & Address)</label>
                        <input type="text" name="instituteName" className="form-control" value={data.instituteName} onChange={(e) => handleChange(setData, data, e)} />
                    </div>
                </div>

                <div className="row">
                    <div className="col-sm-6 form-group">
                        <label>स्कूल / कॉलेज पिनकोड (School PIN Code)</label>
                        <input type="text" name="pincode" maxLength="6" className="form-control" value={data.pincode} onChange={(e) => handleChange(setData, data, e)} />
                    </div>
                </div>
            </div>
        </fieldset>
    );

    return (
        <div id="ctl00_ContentPlaceHolder1_pnlApplicationForm">
            <div className="appstatus" style={{ paddingTop: '0px', marginTop: '0px' }}>
                <div id="ctl00_ContentPlaceHolder1_pnlEducationalQualifications">
                    <h4>
                        <span id="ctl00_ContentPlaceHolder1_lblEducationalSectionHeader">शैक्षिक विवरण (Educational Details)</span>
                    </h4>

                    <div className="form-horizontal">
                        
                        <QualificationBlock 
                            title="10th or equivalent (details as per Class 10th board marksheet)" 
                            data={x10} 
                            setData={setX10}
                            districtOptions={districts10}
                        />

                        <QualificationBlock 
                            title="12th or equivalent (details as per Class 12th board marksheet)" 
                            data={x12} 
                            setData={setX12}
                            districtOptions={districts12}
                        />

                         {/* CAPTCHA */}
                         <h4>CAPTCHA</h4>
                         <div className="form-group row">
                            <label className="control-label col-sm-4">Enter Security Pin</label>
                            <div className="col-sm-4">
                                <input type="text" className="form-control" value={captcha} onChange={(e) => setCaptcha(e.target.value)} required />
                            </div>
                        </div>
                        <div className="form-group row">
                             <label className="control-label col-sm-4"></label>
                             <div className="col-sm-4">
                                  <img src="/assets/cuet-mock/images/DisplayCaptchaImg.jpg" alt="Captcha" style={{height:'34px'}} />
                             </div>
                        </div>

                        <div className="form-group text-center" style={{marginTop:'20px'}}>
                            <button type="button" className="btn btn-warning" onClick={() => navigate('/services/counselling/dashboard/exam-center')} style={{marginRight:'10px'}}>
                                Back
                            </button>
                            <button type="submit" className="btn btn-primary" onClick={handleSubmit} disabled={loading}>
                                {loading ? 'Saving...' : 'Save & Next'}
                            </button>
                        </div>

                    </div>
                </div>
            </div>
        </div>
    );
};

export default Step7Qualification;